import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormSubmitPayload } from 'app/services/contactForm';

import { FormItemType } from '../types';
import useContactForm from './useContactForm';
import useContactFormSubmit from './useContactFormSubmit';
import useContactFormTracking from './useContactFormTracking';
import { ContactFormLinkLocation } from '../stores/ContactFormStore';

interface UseContactFormLogicProps {
  onClose?: () => void;
  onComplete?: () => void;
  contactFormSlug?: string;
  openedThrough?: ContactFormLinkLocation;
}

export default function useContactFormLogic({
  onClose,
  onComplete,
  contactFormSlug,
  openedThrough,
}: UseContactFormLogicProps = {}) {
  const {
    captureContactFormOpened,
    captureContactFormView,
    captureContactFormContinue,
    captureContactFormComplete,
    captureContactFormError,
  } = useContactFormTracking();

  const { data, isFetching } = useContactForm(contactFormSlug);
  const { mutate: submit, isPending } = useContactFormSubmit(contactFormSlug);
  const [submitted, setSubmitted] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const completedRef = useRef(false);

  const forms = useMemo(() => data?.chat_items || [], [data]);
  const formIndex = useMemo(() => forms.findIndex((item) => !item.completed) ?? -1, [forms]);
  const lastForm = useMemo(() => forms[0], [forms]);
  const currentForm = useMemo(() => (formIndex === -1 ? lastForm : forms[formIndex]), [forms, formIndex, lastForm]);
  const currentPage = currentForm?.form_parameters.current_page || 1;
  const totalPages = currentForm?.form_parameters.total_pages || 1;
  const customization = data?.config.customization;
  const [dirtyFields, setDirtyFields] = useState<string[]>([]);

  const hasErrors = useMemo(() => {
    return currentForm?.items.some((item) => item.errors?.length && !dirtyFields.includes(item.ref_id)) || false;
  }, [currentForm, dirtyFields]);

  const setValidity = useCallback((validity: boolean) => validity !== isValid && setIsValid(validity), [isValid]);

  const setFieldDirty = useCallback(
    (field: string) => {
      if (!dirtyFields.includes(field)) {
        setDirtyFields((prev) => [...prev, field]);
      }
    },
    [dirtyFields]
  );

  const handleFormSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();

      if (currentPage === totalPages) {
        onClose?.();
        return;
      }

      setSubmitted(true);

      captureContactFormContinue(currentPage, contactFormSlug, openedThrough);

      const form = event.target as HTMLFormElement;

      const formData = new FormData(form);
      const payload: FormSubmitPayload = [];
      const result: Record<string, string | string[] | boolean> = {};
      const entries = Array.from(formData.entries()) as [string, string][];

      entries.forEach(([key, value]) => {
        const items = formData.getAll(key);
        if (result[key]) return;

        if (items.length > 1) {
          result[key] = items.filter(Boolean) as string[];
        } else {
          const item = currentForm?.items.find((item) => item.ref_id === key);
          if (item && item.item_type === FormItemType.checkbox) {
            result[key] = value === 'on';
          } else {
            result[key] = String(value).trim();
          }
        }
      });

      currentForm?.items.forEach((item) => {
        if (item.item_type === FormItemType.checkbox && !(item.ref_id in result)) {
          result[item.ref_id] = false;
        }
      });

      Object.entries(result).forEach(([key, value]) => {
        payload.push({
          form_ref_id: currentForm?.ref_id ?? '',
          item_ref_id: key,
          value,
        });
      });

      submit(payload, {
        onSuccess: () => {
          if (currentPage === totalPages - 1) {
            captureContactFormComplete(currentPage, contactFormSlug, openedThrough);
          }

          if (currentPage === totalPages) {
            onClose?.();
          }
        },
        onSettled: () => {
          setDirtyFields([]);
        },
      });
    },
    [
      currentForm,
      submit,
      captureContactFormContinue,
      captureContactFormComplete,
      currentPage,
      totalPages,
      onClose,
      contactFormSlug,
      openedThrough,
    ]
  );

  useEffect(() => {
    if (!isFetching && currentForm && currentPage === totalPages && !completedRef.current) {
      completedRef.current = true;
      onComplete?.();
      setIsComplete(true);
    }
  }, [currentPage, totalPages, onComplete, isFetching, currentForm]);

  useEffect(() => {
    setSubmitted(hasErrors);
  }, [currentPage, setSubmitted, hasErrors]);

  useEffect(() => {
    captureContactFormOpened(contactFormSlug, openedThrough);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    captureContactFormView(currentPage, contactFormSlug, openedThrough);
  }, [currentPage, captureContactFormView, contactFormSlug, openedThrough]);

  useEffect(() => {
    if (hasErrors) {
      currentForm?.items.forEach((item) => {
        if (item.errors?.length) {
          captureContactFormError(item.ref_id, item.errors?.join(', '), contactFormSlug);
        }
      });
    }
  }, [hasErrors, captureContactFormError, currentForm, contactFormSlug]);

  return {
    data,
    isFetching,
    isPending,
    currentForm,
    currentPage,
    totalPages,
    customization,
    submitted,
    isValid,
    hasErrors,
    handleFormSubmit,
    setValidity,
    isComplete,
    setFieldDirty,
    dirtyFields,
  };
}
